import { z } from 'zod'

// Orval types admin-hook errors as ErrorResponse, while the mutator rejects
// with an AxiosError whose backend payload lives under response.data.
// Staff-facing BE detail strings render verbatim; see README.md §Raw BE error strings.
const AxiosErrorWithDetailSchema = z.object({
  response: z.object({
    data: z.object({ detail: z.string().min(1) }),
  }),
})

/**
 * The OTHER shape the same field can arrive in.
 *
 * A PATCH that a service guard refuses answers 400 with `ErrorResponse`, whose
 * `detail` is a sentence. A CREATE that a pydantic validator refuses answers 422
 * with Ninja's envelope, whose `detail` is an ARRAY of `{loc, msg, type}` — so
 * the schema above rejects it and the whole rejection used to collapse into the
 * caller's generic fallback. On a form with two dozen controls that reads as
 * "Failed to create announcement" and names nothing.
 *
 * `loc` is the path to the offending field, ending in the field name; the rest
 * of it is envelope (`body`, the parameter name) that means nothing to an
 * operator, so only the last segment is worth showing.
 */
const NinjaValidationErrorSchema = z.object({
  response: z.object({
    data: z.object({
      detail: z
        .array(
          z.object({
            loc: z.array(z.union([z.string(), z.number()])).optional(),
            msg: z.string().min(1),
          }),
        )
        .nonempty(),
    }),
  }),
})

const AxiosErrorWithFieldSchema = z.object({
  response: z.object({
    data: z.object({ data: z.object({ field: z.string().min(1) }) }),
  }),
})

const AxiosErrorWithStatusSchema = z.object({
  response: z.object({ status: z.number() }),
})

const AxiosErrorWithCodeSchema = z.object({
  response: z.object({ data: z.object({ code: z.string().min(1) }) }),
})

const AxiosErrorWithRetryAfterSchema = z.object({
  response: z.object({
    data: z.object({ data: z.object({ retry_after_seconds: z.number().int().positive() }) }),
  }),
})

/**
 * `data.errors` arrives in two shapes. A service-side Django `ValidationError`
 * answers with a `{field: [message]}` map; a request Ninja's own schema
 * validation refuses answers with pydantic's `[{loc, msg}]` list.
 */
const ValidationIssueSchema = z.object({
  loc: z.array(z.union([z.string(), z.number()])).default([]),
  msg: z.string(),
})

/** One pydantic issue: the path to the offending input, and what is wrong with it. */
export type BeValidationIssue = z.infer<typeof ValidationIssueSchema>

const AxiosErrorWithFieldErrorsSchema = z.object({
  response: z.object({
    data: z.object({
      data: z.object({
        errors: z.union([
          z.record(z.string(), z.array(z.string())),
          z.array(ValidationIssueSchema),
        ]),
      }),
    }),
  }),
})

// The backend's own key for an error that belongs to no single field.
export const NON_FIELD_ERRORS_KEY = '__all__'

const FORBIDDEN_STATUS = 403
const CONFLICT_STATUS = 409
const SERVICE_UNAVAILABLE_STATUS = 503

const fieldName = (loc: readonly (string | number)[] | undefined): string | undefined => {
  const last = loc?.at(-1)

  return typeof last === 'string' && last !== '' ? last : undefined
}

const requestErrorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : 'Request failed.'

export const hasBeErrorDetail = (error: unknown): boolean =>
  AxiosErrorWithDetailSchema.safeParse(error).success

/**
 * What the backend said went wrong, or `fallback` when it said nothing readable.
 *
 * Both write paths of the announcement form reach this, and they answer in
 * different shapes — see `NinjaValidationErrorSchema`. Reading only one of them
 * loses the field name on exactly the path where the operator most needs it: a
 * create, where nothing has been saved yet.
 */
export const beErrorDetail = (
  error: unknown,
  fallback: string = requestErrorMessage(error),
): string => {
  const parsed = AxiosErrorWithDetailSchema.safeParse(error)

  if (parsed.success) {
    return parsed.data.response.data.detail
  }

  const validation = NinjaValidationErrorSchema.safeParse(error)

  if (validation.success) {
    return validation.data.response.data.detail
      .map((issue) => {
        const field = fieldName(issue.loc)

        return field === undefined ? issue.msg : `${field}: ${issue.msg}`
      })
      .join('; ')
  }

  return fallback
}

export const beErrorStatus = (error: unknown): number | undefined => {
  const parsed = AxiosErrorWithStatusSchema.safeParse(error)

  return parsed.success ? parsed.data.response.status : undefined
}

export const isForbidden = (error: unknown): boolean => beErrorStatus(error) === FORBIDDEN_STATUS

export const isConflict = (error: unknown): boolean => beErrorStatus(error) === CONFLICT_STATUS

export const isServiceUnavailable = (error: unknown): boolean =>
  beErrorStatus(error) === SERVICE_UNAVAILABLE_STATUS

export const beErrorCode = (error: unknown): string | undefined => {
  const parsed = AxiosErrorWithCodeSchema.safeParse(error)

  return parsed.success ? parsed.data.response.data.code : undefined
}

export const beErrorField = (error: unknown): string | undefined => {
  const parsed = AxiosErrorWithFieldSchema.safeParse(error)

  return parsed.success ? parsed.data.response.data.data.field : undefined
}

export const beErrorRetryAfterSeconds = (error: unknown): number | undefined => {
  const parsed = AxiosErrorWithRetryAfterSchema.safeParse(error)

  return parsed.success ? parsed.data.response.data.data.retry_after_seconds : undefined
}

const locField = (loc: readonly (string | number)[]): string =>
  loc.findLast((segment): segment is string => typeof segment === 'string' && segment !== '') ??
  NON_FIELD_ERRORS_KEY

/**
 * The per-field messages the backend attached, keyed by field name. A list
 * entry whose `loc` names no field lands under `NON_FIELD_ERRORS_KEY`, like
 * the backend's own non-field errors.
 */
export const beFieldErrors = (error: unknown): Record<string, string[]> => {
  const parsed = AxiosErrorWithFieldErrorsSchema.safeParse(error)

  if (!parsed.success) {
    return {}
  }

  const { errors } = parsed.data.response.data.data

  if (!Array.isArray(errors)) {
    return errors
  }

  const fieldErrors: Record<string, string[]> = {}

  errors.forEach(({ loc, msg }) => {
    const field = locField(loc)
    fieldErrors[field] = [...(fieldErrors[field] ?? []), msg]
  })

  return fieldErrors
}

/**
 * The pydantic issues of a list-shaped `data.errors`, in the order the backend
 * sent them, or `[]` when the error carries no such list. For a caller that
 * needs the whole `loc` path, which `beFieldErrors` reduces to a field name.
 */
export const beValidationIssues = (error: unknown): readonly BeValidationIssue[] => {
  const parsed = AxiosErrorWithFieldErrorsSchema.safeParse(error)

  if (!parsed.success) {
    return []
  }

  const { errors } = parsed.data.response.data.data

  return Array.isArray(errors) ? errors : []
}
